/**
 * `/oracle` and `/oracle/<map>` — where the oracle's round for a kitchen is
 * watched.
 *
 * The map name rides in the path, so "watch the oracle on galley" is a link.
 * A name follows the rule maps are saved under (`MapStore`, and the server's
 * `ORACLE_MAP_NAME`): lower-case letters, digits and hyphens, starting with a
 * letter or digit. Anything else under `/oracle/` is `malformed`, and the page
 * says so rather than quietly opening some other kitchen.
 */

import type { OraclePlan } from '@overcooked/shared'

const MAP_NAME = /^[a-z0-9][a-z0-9-]{0,47}$/

/** Matches `/oracle`, `/oracle/` and `/oracle/<anything>[/]`. */
export const ORACLE_PATH = /^\/oracle(?:\/([^/]+))?\/?$/

export type OracleRoute = { kind: 'index' } | { kind: 'map'; map: string } | { kind: 'malformed'; text: string }

/** What an address under `/oracle` asks for, or `null` when it is not under `/oracle` at all. */
export function parseOraclePath(pathname: string): OracleRoute | null {
  const match = ORACLE_PATH.exec(pathname)
  if (!match) return null
  const raw = match[1]
  if (raw === undefined) return { kind: 'index' }
  let name: string
  try {
    name = decodeURIComponent(raw)
  } catch {
    return { kind: 'malformed', text: raw }
  }
  return MAP_NAME.test(name) ? { kind: 'map', map: name } : { kind: 'malformed', text: name }
}

/** The address of a map's watch page. */
export function oraclePathFor(map: string): string {
  return `/oracle/${encodeURIComponent(map)}`
}

/**
 * Where a kitchen's stored round is fetched from: the Oracle's by default, or
 * the baseline's beside it. The Oracle's address names no plan, so it is the
 * same address the page has always asked.
 */
export function logPathFor(map: string, plan: OraclePlan = 'oracle'): string {
  const base = `/api/oracle/${encodeURIComponent(map)}/log`
  return plan === 'baseline' ? `${base}?plan=baseline` : base
}
